<script lang="ts">
	import type { UploadedFile } from "../../../../core/schema/UploadedFile.js";
	import type { ProductPhoto } from "../../../../core/schema/ProductPhoto.js";
	import { createEventDispatcher } from "svelte";
	import ImageUrlInput from "../Form/ImageUrlInput.svelte";
	import FileUploader from "../Form/FileUploader.svelte";
	import DynamicPicture from "../Picture/DynamicPicture.svelte";
	import StaticPicture from "../Picture/StaticPicture.svelte";
	import Icon from "../Icon.svelte";
	import cross from "../../../../core/assets/icons/cross.svg?raw";
	import check from "../../../../core/assets/icons/check.svg?raw";
	import uploadImageFromUrl from "../../appsync/mutations/uploadImageFromUrl.graphql";
	import { createPromiseStore } from "../../../../core/utils/createPromiseStore.js";
	import { getClients } from "../../appsync/getClients.js";
	import type { UploadFromUrlResult } from "../../../../core/schema/ImageLoaderResult.js";
	import preloader from "../../../../core/assets/icons/preloader.svg?raw";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import { ProductPhotoStatus } from "../../../../core/schema/ProductPhotoStatus.js";
	import { acceptedStaticImages } from "../../../../core/schema/acceptedStaticImages.js";

	export let photo: ProductPhoto | undefined;

	const globalSettings = getGlobalSettings();

	const dispatch = createEventDispatcher<{
		change: ProductPhoto | null;
		// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
		clear: void;
		uploading: boolean;
	}>();

	const minimumWidth = 480;
	const minimumHeight = 480;

	let hiddenInput: HTMLInputElement | null = null;
	let naturalWidth: number | undefined;
	let naturalHeight: number | undefined;

	const uploadImage = createPromiseStore(async (url: string) => {
		const { graphQL } = await getClients(globalSettings);
		return graphQL.mutate<{ url: string }, UploadFromUrlResult>({
			mutation: uploadImageFromUrl,
			variables: {
				url,
			},
		});
	});

	function setPhotoFile({ detail: { files } }: CustomEvent<{ files: UploadedFile | undefined }>): void {
		dispatch("change", files ? { id: crypto.randomUUID(), file: files, status: ProductPhotoStatus.ToDo } : null);
	}

	async function setPhotoUrl({ target }: Event): Promise<void> {
		const input = target as HTMLInputElement;
		if (!input.value) {
			dispatch("change", null);
			return;
		}

		dispatch("uploading", true);
		const result = await uploadImage.call(input.value);
		dispatch(
			"change",
			input.value
				? { id: crypto.randomUUID(), url: input.value, file: result.image, status: ProductPhotoStatus.ToDo }
				: null,
		);
		dispatch("uploading", false);
	}

	function clearPhoto(): void {
		naturalWidth = undefined;
		naturalHeight = undefined;
		dispatch("clear");
	}

	$: isSizeBigEnough =
		naturalWidth !== undefined &&
		naturalHeight !== undefined &&
		naturalWidth >= minimumWidth &&
		naturalHeight >= minimumHeight;
	$: isUploading = $uploadImage.status === "loading";
	$: isImageLoaded = Boolean(naturalWidth) && Boolean(naturalHeight);
	$: {
		if (hiddenInput) {
			if (!isImageLoaded) {
				hiddenInput.setCustomValidity("Vyčkejte na načtení obrázku.");
			} else {
				if (!isSizeBigEnough) {
					hiddenInput.setCustomValidity(
						`Obrázek musí mít minimálně šířku ${minimumWidth}px a výšku ${minimumHeight}px.`,
					);
				} else {
					hiddenInput.setCustomValidity("");
				}
			}
		}
	}

	$: hasPhoto = Boolean(photo?.file ?? photo?.url);
</script>

<div class="grid gap-x-3 gap-y-2 text-xs sm:grid-cols-[5.35rem,1fr] sm:gap-x-6 sm:text-base">
	{#if !photo?.file || photo.url}
		<span class="mt-2 leading-[1.2]">Mám odkaz</span>
		<ImageUrlInput id="photo-url" on:change={setPhotoUrl} value={photo?.url} disabled={isUploading} />
	{/if}

	{#if !hasPhoto && !isUploading}
		<span class="mt-2 leading-[1.2]">Mám svůj obrázek</span>
		<FileUploader
			accept={acceptedStaticImages}
			destinationFolder="order-product-photos"
			bucket="upload"
			on:change={setPhotoFile}
		/>
	{:else}
		<span class="mt-2 leading-[1.2]">Náhled obrázku</span>
		<div class="border-1 border-grey-200 relative min-h-48 min-w-48 rounded-lg p-3">
			{#if photo?.url}
				<StaticPicture
					imgClass="mx-auto h-48 w-48 object-cover rounded-full"
					image={photo.url}
					alt="Náhled obrázku"
					width={196}
					height={196}
					bind:naturalWidth
					bind:naturalHeight
				/>
			{:else if photo?.file}
				<DynamicPicture
					imgClass="mx-auto h-48 w-48 object-cover rounded-full"
					image={photo.file}
					bucket="upload"
					width={0}
					height={0}
					alt={photo.file.title ?? "Náhled obrázku"}
					bind:naturalWidth
					bind:naturalHeight
				/>
			{/if}

			{#if isImageLoaded}
				<div class="text-grey-300 text-2xs mt-3 flex items-start gap-2 leading-[1.2]">
					{#if !isSizeBigEnough}
						<p>
							Obrázek je příliš malý. Vyžadujeme obrázek s minimální šířkou {minimumWidth}px a výškou {minimumHeight}px.
						</p>
					{:else}
						<p>Obrázek má skvělé parametry, tisk je možný.</p>
					{/if}
					<div class="flex flex-1 items-center gap-1">
						{#if isSizeBigEnough}
							<Icon icon={check} class="text-tertiary mt-1 h-3 w-3" />
						{/if}
						<button class="mt-[.3rem]" type="button" on:click={clearPhoto}>
							<Icon icon={cross} class="h-2 w-2" />
						</button>
					</div>
				</div>
			{:else}
				<div class="absolute top-12 flex h-24 w-full items-center justify-center">
					<Icon icon={preloader} class="w-20" />
				</div>
			{/if}
			<input
				type="text"
				class="pointer-events-none absolute opacity-0"
				aria-hidden="true"
				bind:this={hiddenInput}
				tabindex="-1"
			/>
		</div>
	{/if}
</div>
